import React, {useEffect, useState} from "react";
import {useDownloadURL} from 'react-firebase-hooks/storage';
import clsx from "clsx";
import {Button, FileInput, Label} from "flowbite-react";
import {storage} from 'hooks/firebase';
import {ref, ref as storageRef, uploadBytes} from 'firebase/storage';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faUpload, faX} from "@fortawesome/free-solid-svg-icons";
import {getAuth} from "firebase/auth";
import {useAuthState} from "react-firebase-hooks/auth";
import {convertFileToImageSrc} from "helpers/file-to-image-src";
import {isNullOrUndefined} from "helpers/is-null-or-undefined";
import {RenderableTitle} from "data/models/survey/base";
import {QUESTION_CONTAINER_CLASS} from "components/Survey/classes";
import LoadingSurvey from "components/Survey/LoadingSurvey";
import {assertNoError} from "helpers/asserts";
import {StorageFileValue} from "data/models/survey/file-upload";

export type FileUpload_SurveyQuestionPage_Props = {
  visible: boolean,
  questionId: string,
  required: boolean,
  title: RenderableTitle,
  description: RenderableTitle | null,
  value: StorageFileValue | undefined,
  onChange: (id: string, value: StorageFileValue | null) => void,
}

const fileInputTheme = {
  "field": {
    "input": {
      "base": "cursor-pointer block w-full h-full overflow-hidden rounded-lg border disabled:cursor-not-allowed disabled:opacity-50",
    }
  }
};

export default function FileUploadSurveyQuestionPage(
    {
      visible,
      questionId,
      required,
      title,
      description,
      value,
      onChange
    }: FileUpload_SurveyQuestionPage_Props
) {
  const auth = getAuth();
  const [user, userLoading, userError] = useAuthState(auth);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedFilePath, setSelectedFilePath] = useState<string | null>(null);
  const [filePath, setFilePath] = useState<string | null>(value?.filePath ?? null);
  const [fileUrl, loading, error] = useDownloadURL(filePath ? storageRef(storage, filePath) : null);

  useEffect(() => {
    if (selectedFile === null) {
      setSelectedFilePath(null);
      return;
    }
    convertFileToImageSrc(selectedFile).then((url) => setSelectedFilePath(url));
  }, [selectedFile]);

  useEffect(() => {
    if (!isNullOrUndefined(value) || isNullOrUndefined(fileUrl) || isNullOrUndefined(filePath)) {
      return;
    }
    onChange(questionId, {
      downloadUrl: fileUrl,
      filePath,
    });

  }, [fileUrl, filePath, value]);

  if (loading || isNullOrUndefined(user) || userLoading) {
    return visible && <LoadingSurvey/> || null;
  }
  assertNoError(
      error
      ?? userError
  );

  function uploadFile() {
    if (isNullOrUndefined(user)) {
      throw new Error("Invalid app state, user is not defined.");
    }
    if (selectedFile === null) {
      throw new Error("Invalid app state, selectedFile is not defined.");
    }
    const filePathToUpload = `user_files/${user.uid}/${Date.now()}-${selectedFile.name}`;
    const fileToUploadRef = ref(storage, filePathToUpload);

    uploadBytes(fileToUploadRef, selectedFile).then(() => {
      setFilePath(filePathToUpload);
      setSelectedFile(null);
    });
  }

  function removeStoredFile() {
    onChange(questionId, null)
    setSelectedFile(null);
    setFilePath(null);

  }

  return <div className={"flex flex-col w-full"}>
    <div className={QUESTION_CONTAINER_CLASS}>
      <div className={clsx("my-4", "text-center")}>{title}</div>
      {description !== null && <div className={clsx("my-4", "text-center")}>{description}</div>}
      {(fileUrl !== undefined) && (
          <div className={"flex flex-col"}>
            <div className={"mx-auto"}>
              <div className={"flex flex-row-reverse"}>
                <Button
                    color={"blue"}
                    className={"mb-2"}
                    onClick={() => removeStoredFile()}
                >
                  <FontAwesomeIcon icon={faX} color={"white"} size={"xl"}/>
                </Button>
              </div>
              <div className={"border-2 p-2"}>
                <img src={fileUrl} alt={"Uploaded file"}/>
              </div>
            </div>
          </div>
      )}
      {(selectedFile !== null) && (
          <div className={"flex flex-col"}>
            <div className={"flex flex-col"}>
              <div className={"mx-auto"}>
                <div className={"flex flex-row-reverse"}>
                  <Button
                      color={"blue"}
                      className={"mb-2"}
                      onClick={() => setSelectedFile(null)}
                  >
                    <FontAwesomeIcon icon={faX} color={"white"} size={"xl"}/>
                  </Button>
                </div>
                <div className={"border-2 p-2"}>
                  <img src={selectedFilePath ?? ""} alt={"File to Upload"}/>
                </div>
              </div>
            </div>
            <div>
              <Button
                  onClick={() => uploadFile()}
                  className={"w-1/2 mx-auto px-8 py-2 mt-8 rounded-full"}
                  color={"blue"}
              >
                <span className={"text-xl font-bold"}>Upload</span>
                <FontAwesomeIcon icon={faUpload} className={"ml-4 text-white"} size={"2xl"}/>
              </Button>
            </div>
          </div>
      )}
      {fileUrl === undefined && selectedFile === null && (
          <div className="flex relative w-full f-dull items-center justify-center">
            <Label
                htmlFor={questionId}
                className="relative flex h-64 w-full flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 hover:bg-gray-100"
            >
              <div className="flex flex-col items-center justify-center pb-6 pt-5 gap-4">
                <div>
                  <p className="mb-2 text-xl font-normal text-gray-500 text-center">
                    Drag &amp; Drop files here
                  </p>
                </div>
                <div className={"w-8 h-8 mb-8"}>
                  <FontAwesomeIcon icon={faUpload} className={"w-12 h-12 text-bc-blue"} size={"xl"}/>
                </div>
                <div>
                  <p className="text-xl font-normal text-gray-500 text-center">
                    or Click to upload from computer.
                  </p>
                </div>
              </div>
              <FileInput
                  id={questionId}
                  className={"absolute top-0 left-0 w-full h-full opacity-0"}
                  theme={fileInputTheme}
                  required={required}
                  sizing={"lg"}
                  helperText={"SVG, PNG, JPG or GIF."}
                  onChange={({target: {files}}) => setSelectedFile(files?.[0] ?? null)}
              />
            </Label>
          </div>)}
    </div>
  </div>
}
